import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import api, { formatDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const CATEGORIES = ["Všetky", "ŠTK", "DK", "KR", "Obsadenie R", "Výkonný výbor", "Odvolacia komisia", "Sekretár", "TMK", "KM", "MaK"];

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("Všetky");
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");

  useEffect(() => {
    api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const years = useMemo(() => {
    const set = new Set(items.map((a) => (a.date || "").slice(0, 4)).filter(Boolean));
    return ["all", ...Array.from(set).sort((a, b) => b.localeCompare(a))];
  }, [items]);

  const filtered = items.filter((a) => {
    const okCat = cat === "Všetky" || a.category === cat;
    const okYear = year === "all" || (a.date || "").slice(0, 4) === year;
    const okQ = !q || a.title.toLowerCase().includes(q.toLowerCase()) || a.excerpt.toLowerCase().includes(q.toLowerCase());
    return okCat && okYear && okQ;
  });

  return (
    <div data-testid="announcements-page">
      <Seo title="Úradné správy" description="Aktuálne rozhodnutia a oznamy odborných komisií ObFZ Levice — ŠTK, DK, KR, obsadenie rozhodcov a ďalšie. Archív úradných správ." />
      <PageHeader
        eyebrow="Komisie ObFZ Levice"
        title="Úradné správy"
        description="Aktuálne rozhodnutia ŠTK, DK, KR a ďalších odborných komisií. Archív zoradený od najnovších."
        breadcrumbs={[{ label: "Úradné správy" }]}
      />

      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Hľadať v správach..." className="input pl-9"
                     data-testid="search-announcements" />
            </div>
            <div className="relative">
              <select value={year} onChange={(e) => setYear(e.target.value)}
                      className="input pr-8 mono text-[11px] font-bold uppercase tracking-wider cursor-pointer"
                      data-testid="announcement-year-filter" aria-label="Filter podľa roku">
                {years.map((y) => (
                  <option key={y} value={y}>{y === "all" ? "Všetky roky" : y}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-1 overflow-x-auto no-scrollbar">
              {CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => setCat(c)}
                        className={`px-3 py-2 mono text-[11px] font-bold uppercase tracking-wider border transition-colors flex-shrink-0 ${
                          cat === c ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:border-ink-900"
                        }`}
                        data-testid={`filter-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-ink-100 divide-y divide-ink-100">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-ink-500">Žiadne správy nezodpovedajú filtru.</div>
            ) : (
              filtered.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/uradne-spravy/${a.id}`}
                  className="grid grid-cols-12 items-start gap-4 px-5 py-5 hover:bg-cream transition-colors group"
                  data-testid={`announcement-${a.id}`}
                >
                  <div className="col-span-1 mono text-[11px] font-bold text-ink-400">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-3 md:col-span-2 mono text-xs text-ink-500">
                    {formatDate(a.date)}
                  </div>
                  <div className="col-span-8 md:col-span-2">
                    <span className="mono text-[10px] font-bold uppercase tracking-widest text-green-700">
                      {a.category}
                    </span>
                    {a.number && <span className="mono text-[10px] text-ink-400 ml-1">· {a.number}</span>}
                  </div>
                  <div className="col-span-12 md:col-span-6 -mt-2 md:mt-0">
                    <h3 className="font-heading font-bold text-ink-900 text-base leading-tight group-hover:text-green-700 transition-colors">
                      {a.title}
                    </h3>
                    <p className="text-xs text-ink-500 line-clamp-1 mt-1">{a.excerpt}</p>
                  </div>
                  <div className="col-span-12 md:col-span-1 text-right -mt-3 md:mt-0">
                    <ChevronRight className="w-4 h-4 text-ink-400 inline group-hover:text-green-700" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
